import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { GetPolicyWalletBalanceForUser, PostPolicyWalletAddMoney } from "../../Service/_index";
import { ToastError, ToastInfo, ToastSuccess } from "../../UiComponents/Toaster/Toast";
import { AddMoneySuccessDialog, ConfirmAddMoneyDialog } from "./AddMoneyDialogs";
import ProofPicker from "./ProofPicker";
import UserAutocomplete from "./UserAutocomplete";
import {
  MAX_REFERENCE_LENGTH,
  MAX_REMARKS_LENGTH,
  PAYMENT_MODES,
  QUICK_AMOUNTS,
  addMoneySchema,
  addToAmount,
  buildAddMoneyFormData,
  computeBalanceAfter,
  createAttemptTracker,
  formatINR,
  paymentModeLabel,
  referenceLabelFor,
  sanitizeAmountInput,
  shouldReuseRequestId,
  validateReceiptFile,
} from "./addMoneyUtils";
import { FieldLabel, inputSx, navyButtonSx, outlinedButtonSx } from "./parts";
import Styles from "./AddMoneyToUser.module.scss";

const INITIAL_VALUES = {
  user: null,
  amount: "",
  paymentMode: "",
  reference: "",
  remarks: "",
  receipt: null,
};

// The picker list already carries a balance, so the summary can show one at
// once; the live lookup then replaces it with the current figure.
const useCurrentBalance = (user) => {
  const userId = user?._id;
  const listBalance = user?.balance;
  const [state, setState] = useState({ value: null, loading: false, stale: false });

  useEffect(() => {
    if (!userId) {
      setState({ value: null, loading: false, stale: false });
      return undefined;
    }
    let cancelled = false;
    const fallback = Number(listBalance) || 0;
    setState({ value: fallback, loading: true, stale: false });
    GetPolicyWalletBalanceForUser(userId).then((res) => {
      if (cancelled) return;
      const fresh = res?.balance ?? res?.data?.balance;
      if (res?.error || fresh === undefined || fresh === null || Number.isNaN(Number(fresh))) {
        setState({ value: fallback, loading: false, stale: true });
      } else {
        setState({ value: Number(fresh), loading: false, stale: false });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userId, listBalance]);

  return state;
};

const SummaryRow = ({ label, children }) => (
  <>
    <dt className={Styles.SummaryKey}>{label}</dt>
    <dd className={Styles.SummaryValue}>{children}</dd>
  </>
);

const Placeholder = () => <span className={Styles.Muted}>—</span>;

const SummaryPanel = ({ values, balance }) => {
  const { user, amount, paymentMode, reference, receipt } = values;
  const after = computeBalanceAfter(balance.value, amount);
  const amountOk = after !== null;

  return (
    <Box className={Styles.Summary} component="aside" aria-label="Summary of this top-up">
      <Box className={Styles.SummaryHeader}>
        <Typography className={Styles.SummaryTitle}>Summary</Typography>
        <Typography className={Styles.SummarySubtitle}>Updates as you fill in the form</Typography>
      </Box>
      <Box className={Styles.SummaryBody}>
        <Box className={Styles.BalanceBox} aria-live="polite">
          <Box className={Styles.BalanceCell}>
            <span className={Styles.BalanceLabel}>Current balance</span>
            <span className={Styles.BalanceValue}>{user ? formatINR(balance.value) : <Placeholder />}</span>
          </Box>
          <span aria-hidden="true">&rarr;</span>
          <Box className={Styles.BalanceCell}>
            <span className={Styles.BalanceLabel}>New balance</span>
            <span className={`${Styles.BalanceValue} ${amountOk ? Styles.BalanceAfterUp : ""}`}>
              {user && amountOk ? formatINR(after) : <Placeholder />}
            </span>
          </Box>
          {balance.loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%", fontSize: "11.5px" }}>
              <CircularProgress size={11} sx={{ color: "#667085" }} /> Refreshing balance…
            </Box>
          )}
          {balance.stale && (
            <Box sx={{ width: "100%", fontSize: "11.5px", color: "#B54708" }}>
              Could not refresh the balance — showing the last known figure.
            </Box>
          )}
        </Box>

        <Box component="dl" className={Styles.SummaryRows} sx={{ m: 0 }}>
          {user && user.adminName && <SummaryRow label="Admin">{user.adminName}</SummaryRow>}
          <SummaryRow label="User">
            {user ? (
              <>
                {user.name}
                <Box sx={{ fontSize: "11.5px", color: "#667085", fontWeight: 400 }}>
                  {[user.mobileNumber, user.email].filter(Boolean).join(" · ")}
                </Box>
              </>
            ) : (
              <Placeholder />
            )}
          </SummaryRow>
          <SummaryRow label="Amount">{amountOk ? formatINR(amount) : <Placeholder />}</SummaryRow>
          <SummaryRow label="Payment mode">{paymentMode ? paymentModeLabel(paymentMode) : <Placeholder />}</SummaryRow>
          {reference.trim() && <SummaryRow label={referenceLabelFor(paymentMode)}>{reference.trim()}</SummaryRow>}
          {receipt && <SummaryRow label="Proof">{receipt.name}</SummaryRow>}
        </Box>
      </Box>
    </Box>
  );
};

const AddMoneyForm = ({
  users,
  usersLoading,
  usersError,
  onRetryUsers,
  onCredited,
  superAdmin = false,
  admins = [],
  adminFilter = null,
  onAdminFilter,
}) => {
  const tracker = useRef(null);
  if (!tracker.current) tracker.current = createAttemptTracker();
  const inFlight = useRef(false);

  const [pending, setPending] = useState(null); // details awaiting the confirm dialog
  const [posting, setPosting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [receiptError, setReceiptError] = useState("");
  const [success, setSuccess] = useState(null);

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: addMoneySchema,
    onSubmit: (values, helpers) => {
      helpers.setSubmitting(false);
      setPending({
        user: values.user,
        userId: values.user._id,
        // the super admin's pick carries its admin; an admin's own users do not (the server uses the login's client)
        adminClientId: superAdmin ? values.user.clientId : undefined,
        amount: values.amount,
        paymentMode: values.paymentMode,
        reference: values.reference.trim(),
        remarks: values.remarks.trim(),
        receipt: values.receipt,
      });
    },
  });
  const { values, setFieldValue, handleBlur, handleChange } = formik;
  const balance = useCurrentBalance(values.user);

  // Any edit makes a previous server error stale.
  useEffect(() => {
    setSubmitError(null);
  }, [values]);

  const showError = (name) => (formik.touched[name] || formik.submitCount > 0) && formik.errors[name];

  const clearForm = () => {
    formik.resetForm();
    setReceiptError("");
    setSubmitError(null);
  };

  const handleClear = () => {
    tracker.current.reset();
    clearForm();
  };

  const pickReceipt = (file) => {
    const problem = validateReceiptFile(file);
    setReceiptError(problem);
    if (!problem) setFieldValue("receipt", file);
  };

  const handleConfirm = async () => {
    if (inFlight.current || !pending) return; // a double click must not post twice
    inFlight.current = true;
    setPosting(true);
    setSubmitError(null);

    const details = pending;
    // Same user and amount keep the same key, so a retry after a lost response cannot credit twice.
    const requestId = tracker.current.idFor(details);
    const res = await PostPolicyWalletAddMoney(buildAddMoneyFormData(details, requestId));

    inFlight.current = false;
    setPosting(false);
    setPending(null);

    if (res.error) {
      // Only a lost response or an attempt still running may retry with this id;
      // after any other answer it may be burnt (REQUEST_FAILED, REQUEST_ID_REUSED).
      if (!shouldReuseRequestId(res)) tracker.current.reset();
      let message = res.message || "Could not add money. Please try again.";
      if (res.code === "IN_PROGRESS") {
        message = "Your previous attempt is still being processed. Wait a moment, then press Add Money again.";
      } else if (res.noResponse) {
        message =
          "We could not confirm whether the money was added. Check Recent additions, or press Add Money again — retrying will not add it twice.";
      }
      const calm = res.code === "IN_PROGRESS" || res.noResponse || /turned off/i.test(message);
      setSubmitError({ message, tone: calm ? "warning" : "error" });
      ToastError(message);
      return;
    }

    tracker.current.reset();
    clearForm();
    const data = res.data || {};
    if (data.duplicate) {
      ToastInfo("This top-up was already added");
    } else {
      const result = {
        ...data,
        userName: data.userName || details.user.name,
        amount: data.amount ?? details.amount,
        paymentMode: data.paymentMode || details.paymentMode,
        reference: data.reference ?? details.reference,
        balanceAfter: data.balanceAfter ?? computeBalanceAfter(balance.value, details.amount),
      };
      setSuccess(result);
      ToastSuccess(`${formatINR(result.amount)} added to ${result.userName}'s Policy Wallet`);
    }
    onCredited();
  };

  const referenceLabel = referenceLabelFor(values.paymentMode);

  return (
    <>
      <Box className={Styles.Body}>
        <Box
          component="form"
          noValidate
          onSubmit={formik.handleSubmit}
          className={Styles.FormStack}
          aria-busy={posting}
        >
          {superAdmin && (
            <Box>
              <FieldLabel htmlFor="add-money-admin" optional>
                Admin
              </FieldLabel>
              <Autocomplete
                id="add-money-admin"
                size="small"
                fullWidth
                openOnFocus
                options={admins}
                value={adminFilter}
                disabled={posting}
                onChange={(_, admin) => {
                  onAdminFilter(admin);
                  // a user of another admin no longer fits the filter
                  if (admin && values.user && values.user.clientId !== admin._id) setFieldValue("user", null);
                }}
                getOptionLabel={(admin) => admin.name || ""}
                isOptionEqualToValue={(option, selected) => option._id === selected._id}
                noOptionsText="No admin has user logins"
                renderOption={(props, admin) => (
                  <Box component="li" {...props} key={admin._id} sx={{ fontFamily: "Inter", fontSize: "13px" }}>
                    {admin.name}
                    <Box component="span" sx={{ ml: "auto", pl: 2, color: "#667085", fontSize: "12px" }}>
                      {admin.userCount} {admin.userCount === 1 ? "user" : "users"}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All admins"
                    inputProps={{ ...params.inputProps, "aria-label": "Filter users by admin" }}
                    sx={inputSx}
                  />
                )}
              />
            </Box>
          )}

          <Box>
            <FieldLabel htmlFor="add-money-user" required>
              User
            </FieldLabel>
            <UserAutocomplete
              id="add-money-user"
              value={values.user}
              onChange={(user) => setFieldValue("user", user)}
              onBlur={() => formik.setFieldTouched("user", true)}
              options={users}
              loading={usersLoading}
              disabled={posting}
              placeholder="Search by name, mobile or email"
              error={Boolean(showError("user"))}
              helperText={showError("user") || undefined}
            />
            {usersError && !usersLoading && (
              <Box role="alert" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                <Typography sx={{ flex: 1, fontFamily: "Inter", fontSize: "12px", color: "#B42318" }}>
                  {usersError}
                </Typography>
                <Button size="small" variant="outlined" onClick={onRetryUsers} sx={{ ...outlinedButtonSx, height: 28 }}>
                  Retry
                </Button>
              </Box>
            )}
            {!usersError && !usersLoading && users.length === 0 && (
              <Typography sx={{ mt: 0.75, fontFamily: "Inter", fontSize: "12px", color: "#667085" }}>
                {superAdmin
                  ? adminFilter
                    ? "This admin has no enabled user logins."
                    : "No admin has enabled user logins yet."
                  : "No enabled user logins found for your account yet."}
              </Typography>
            )}
          </Box>

          <Box className={Styles.FieldPair}>
            <Box>
              <FieldLabel htmlFor="add-money-amount" required>
                Amount
              </FieldLabel>
              <TextField
                id="add-money-amount"
                name="amount"
                fullWidth
                size="small"
                placeholder="0.00"
                autoComplete="off"
                value={values.amount}
                onChange={(event) => setFieldValue("amount", sanitizeAmountInput(event.target.value))}
                onBlur={handleBlur}
                disabled={posting}
                error={Boolean(showError("amount"))}
                helperText={showError("amount") || undefined}
                inputProps={{ inputMode: "decimal" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span aria-hidden="true">₹</span>
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />
              <Box className={Styles.QuickChips}>
                <span className={Styles.QuickLabel}>Quick add</span>
                {QUICK_AMOUNTS.map((step) => (
                  <Chip
                    key={step}
                    size="small"
                    variant="outlined"
                    clickable
                    disabled={posting}
                    label={`+${step.toLocaleString("en-IN")}`}
                    aria-label={`Add ${step.toLocaleString("en-IN")} rupees to the amount`}
                    onClick={() => {
                      setFieldValue("amount", addToAmount(values.amount, step));
                      formik.setFieldTouched("amount", true, false);
                    }}
                    sx={{
                      fontFamily: "Inter",
                      fontSize: "12px",
                      color: "#1E3A5F",
                      borderColor: "#D0D5DD",
                      "&:hover": { backgroundColor: "#EEF3F9" },
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <FieldLabel id="add-money-mode-label" required>
                Payment mode
              </FieldLabel>
              <TextField
                select
                id="add-money-mode"
                name="paymentMode"
                fullWidth
                size="small"
                value={values.paymentMode}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={posting}
                error={Boolean(showError("paymentMode"))}
                helperText={showError("paymentMode") || undefined}
                SelectProps={{
                  displayEmpty: true,
                  labelId: "add-money-mode-label",
                  renderValue: (value) =>
                    value ? (
                      paymentModeLabel(value)
                    ) : (
                      <Box component="span" sx={{ color: "#98A2B3" }}>
                        Select payment mode
                      </Box>
                    ),
                }}
                sx={inputSx}
              >
                {PAYMENT_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Box>
            <FieldLabel htmlFor="add-money-reference" optional>
              {referenceLabel}
            </FieldLabel>
            <TextField
              id="add-money-reference"
              name="reference"
              fullWidth
              size="small"
              autoComplete="off"
              placeholder={referenceLabel === "Reference no." ? "e.g. receipt or voucher number" : "e.g. UTR or cheque number"}
              value={values.reference}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={posting}
              error={Boolean(showError("reference"))}
              helperText={showError("reference") || undefined}
              inputProps={{ maxLength: MAX_REFERENCE_LENGTH }}
              sx={inputSx}
            />
          </Box>

          <Box>
            <FieldLabel htmlFor="add-money-remarks" optional>
              Remarks
            </FieldLabel>
            <TextField
              id="add-money-remarks"
              name="remarks"
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              placeholder="Anything worth remembering about this top-up"
              value={values.remarks}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={posting}
              error={Boolean(showError("remarks"))}
              helperText={
                <Box component="span" sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <span>{showError("remarks") || ""}</span>
                  <span>
                    {values.remarks.length}/{MAX_REMARKS_LENGTH}
                  </span>
                </Box>
              }
              inputProps={{ maxLength: MAX_REMARKS_LENGTH }}
              sx={inputSx}
            />
          </Box>

          <Box>
            <FieldLabel htmlFor="add-money-proof" optional>
              Proof
            </FieldLabel>
            <ProofPicker
              id="add-money-proof"
              file={values.receipt}
              onPick={pickReceipt}
              onRemove={() => {
                setFieldValue("receipt", null);
                setReceiptError("");
              }}
              error={receiptError}
              disabled={posting}
            />
          </Box>

          {submitError && (
            <Alert severity={submitError.tone} sx={{ fontFamily: "Inter", fontSize: "13px" }}>
              {submitError.message}
            </Alert>
          )}

          <Box className={Styles.FormActions}>
            <Button
              type="button"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleClear}
              disabled={posting}
              sx={outlinedButtonSx}
            >
              Clear
            </Button>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={posting}
              startIcon={
                posting ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <AddIcon sx={{ fontSize: "16px !important" }} />
                )
              }
              sx={navyButtonSx}
            >
              Add Money
            </Button>
          </Box>
        </Box>

        <SummaryPanel values={values} balance={balance} />
      </Box>

      <ConfirmAddMoneyDialog
        open={Boolean(pending)}
        details={pending}
        currentBalance={balance.value}
        balanceAfter={pending ? computeBalanceAfter(balance.value, pending.amount) : null}
        posting={posting}
        onCancel={() => setPending(null)}
        onConfirm={handleConfirm}
      />
      <AddMoneySuccessDialog result={success} onClose={() => setSuccess(null)} />
    </>
  );
};

export default AddMoneyForm;
